import { htmlSafe } from '@ember/template';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { formatNumber, t } from 'ember-intl';
import BvTooltip from 'volta/components/bv-tooltip';
import classNames from 'volta/helpers/class-names';
import orElse from 'volta/helpers/or-else';
import { round } from 'volta/utils/math-utils';

type TPosition = 'top' | 'bottom' | 'left' | 'right';

const LEVEL_POSITIONS: TPosition[] = ['top', 'bottom', 'left', 'right'];

export interface PercentGaugeSignature {
  Args: {
    fullWidth?: boolean;
    level?: number;
    criticality?: string;
    showLevel?: boolean;
    showLevelInTooltip?: boolean;
    levelPosition?: TPosition;
    displayEmptyText?: boolean;
    emptyTextLabel?: string;
    bordered?: boolean;
    rounded?: boolean;
    small?: boolean;
    title?: string;
    tooltipSubtext?: string;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

export default class PercentGaugeComponent extends Component<PercentGaugeSignature> {
  // Services
  // ~~~~~

  // Properties
  // ~~~~~

  get showLevel() {
    return this.args.showLevel ?? true;
  }

  get levelPosition() {
    return this.args.levelPosition ?? 'top';
  }

  get emptyTextLabel() {
    return this.args.emptyTextLabel ?? 'none';
  }

  get criticalityClass() {
    const { level, criticality } = this.args;
    return isPresent(level)
      ? isPresent(criticality)
        ? `percent-gauge--${criticality}`
        : undefined
      : 'percent-gauge--none';
  }

  get levelClass() {
    const { levelPosition } = this;
    if (levelPosition && LEVEL_POSITIONS.includes(levelPosition.toLowerCase() as TPosition)) {
      return `percent-gauge--level-${levelPosition.toLowerCase()}`;
    }
    return undefined;
  }

  get tooltipClass() {
    return isPresent(this.args.criticality) ? `u-bg-${this.args.criticality}` : undefined;
  }

  get isFilled() {
    return this.args.level && this.args.level >= 1;
  }

  get fillStyle() {
    const level = this.args.level ?? 0;
    const width = level > 1 ? 1 : level < 0 ? 0 : level;
    return htmlSafe(`width:${(round(width, 2) ?? 0) * 100}%;`);
  }

  get isInverse() {
    return this.args.level && this.args.level <= 0.5;
  }

  <template>
    <div
      class={{classNames
        'percent-gauge'
        this.criticalityClass
        this.levelClass
        (if this.isFilled 'percent-gauge--filled')
        (if @bordered 'percent-gauge--bordered')
        (if @rounded 'percent-gauge--rounded')
        (if @small 'percent-gauge--sm')
        (if @fullWidth 'percent-gauge--full-width')
      }}
      ...attributes
    >
      {{#if this.showLevel}}
        <div class='percent-gauge__level'>
          {{#if @criticality}}
            {{orElse @title (if (isPresent @level) (formatNumber @level style='percent') '—')}}
          {{else if @displayEmptyText}}
            {{t this.emptyTextLabel}}
          {{/if}}
        </div>
      {{/if}}
      <div class='percent-gauge__bg'>
        <span class='percent-gauge__fill' style={{this.fillStyle}}></span>
      </div>
      {{#if @showLevelInTooltip}}
        <BvTooltip
          class={{this.tooltipClass}}
          @text={{if
            @criticality
            (if (isPresent @level) (formatNumber @level style='percent') '—')
            (if @displayEmptyText (t this.emptyTextLabel))
          }}
          @subtext={{@tooltipSubtext}}
        />
      {{/if}}
      {{yield}}
    </div>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    PercentGauge: typeof PercentGaugeComponent;
    'percent-gauge': typeof PercentGaugeComponent;
  }
}
